import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Button, Chip, Divider, Grid, LinearProgress, Paper, Stack, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import Spinner from 'src/utils/Loader/spinner';
import { useToaster } from 'src/utils/toaster/toasterContext';

const getSetupStepMeta = (translate) => ({
  workspace_created: {
    label: translate('support_setup_page.steps.workspace.label', { defaultValue: 'Workspace' }),
    title: translate('support_setup_page.steps.workspace.title', { defaultValue: 'Workspace created' }),
    description: translate('support_setup_page.steps.workspace.description', { defaultValue: 'Organization and workspace are ready for support operations.' }),
    icon: <ChatBubbleOutlineIcon />,
  },
  widget_configured: {
    label: translate('support_setup_page.steps.widget.label', { defaultValue: 'Widget' }),
    title: translate('support_setup_page.steps.widget.title', { defaultValue: 'Chat widget' }),
    description: translate('support_setup_page.steps.widget.description', { defaultValue: 'Customer chat widget is enabled for the active workspace.' }),
    icon: <TuneIcon />,
    path: '/widget',
  },
  faq_ready: {
    label: translate('support_setup_page.steps.faq.label', { defaultValue: 'FAQ' }),
    title: translate('support_setup_page.steps.faq.title', { defaultValue: 'FAQ setup' }),
    description: translate('support_setup_page.steps.faq.description', { defaultValue: 'At least one FAQ item is enabled for customer self-service.' }),
    icon: <HelpOutlineIcon />,
    path: '/faq',
  },
  kb_ready: {
    label: translate('support_setup_page.steps.kb.label', { defaultValue: 'Knowledge Base' }),
    title: translate('support_setup_page.steps.kb.title', { defaultValue: 'Knowledge base' }),
    description: translate('support_setup_page.steps.kb.description', { defaultValue: 'At least one help article is published.' }),
    icon: <MenuBookIcon />,
    path: '/kb-info',
  },
  ai_chat_enabled: {
    label: translate('support_setup_page.steps.ai_chat.label', { defaultValue: 'AI Chat' }),
    title: translate('support_setup_page.steps.ai_chat.title', { defaultValue: 'AI chat' }),
    description: translate('support_setup_page.steps.ai_chat.description', { defaultValue: 'AI chat is enabled for the workspace.' }),
    icon: <SmartToyIcon />,
    path: '/ai-chat',
  },
  live_chat_enabled: {
    label: translate('support_setup_page.steps.live_chat.label', { defaultValue: 'Live Chat' }),
    title: translate('support_setup_page.steps.live_chat.title', { defaultValue: 'Live chat' }),
    description: translate('support_setup_page.steps.live_chat.description', { defaultValue: 'Enable real-time agent chat and inbox.' }),
    icon: <ChatBubbleOutlineIcon />,
    path: '/live-chat',
  },
  ticket_smtp_configured: {
    label: translate('support_setup_page.steps.ticket.label', { defaultValue: 'Ticket' }),
    title: translate('support_setup_page.steps.ticket.title', { defaultValue: 'Ticket setup' }),
    description: translate('support_setup_page.steps.ticket.description', { defaultValue: 'SMTP is configured for ticket email replies.' }),
    icon: <ConfirmationNumberIcon />,
    path: '/ticket-setup',
  },
});

const ChatSetup = () => {
  const { t: translate } = useTranslation();
  const [loader, setLoader] = useState(true);
  const [setupStatus, setSetupStatus] = useState(null);
  const { showToast } = useToaster();
  const theme = useTheme();
  const navigate = useNavigate();
  const setupStepMeta = useMemo(() => getSetupStepMeta(translate), [translate]);
  const defaultSteps = useMemo(
    () =>
      Object.keys(setupStepMeta).map((key) => ({
        key,
        label: setupStepMeta[key].label,
        is_complete: false,
        completed_by: 0,
      })),
    [setupStepMeta]
  );

  const fetchSetupStatus = useCallback(() => {
    setLoader(true);
    HttpServices.Get({
      url: userModuleURL.getChatSetupStatus,
      successCallback: (data) => {
        setSetupStatus(data);
        setLoader(false);
      },
      failureCallback: (message) => {
        setLoader(false);
        message && showToast(message, 'e');
      },
    });
  }, []);

  useEffect(() => {
    fetchSetupStatus();
  }, [fetchSetupStatus]);

  const steps = useMemo(() => {
    const apiSteps = setupStatus?.steps?.length ? setupStatus.steps : defaultSteps;
    return apiSteps.map((step) => ({
      ...step,
      ...(setupStepMeta[step.key] || {}),
    }));
  }, [defaultSteps, setupStatus, setupStepMeta]);

  const completedSteps = steps.filter((step) => step.is_complete).length;
  const progress = steps.length ? Math.round((completedSteps / steps.length) * 100) : 0;
  const nextStep = steps.find((step) => step.key === setupStatus?.next_step && !step.is_complete);

  return (
    <>
      <Helmet>
        <title>{translate('support_setup_page.quick_setup', { defaultValue: 'Quick Setup' })}</title>
      </Helmet>

      {loader ? (
        <Spinner height={window.innerHeight - 110} />
      ) : (
        <Stack spacing={3} sx={{ pb: 12 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
              <Box>
                <Typography variant="h5">Quick Setup</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {nextStep
                    ? translate('support_setup_page.next_step', { step: nextStep.title, defaultValue: 'Next step: {{step}}' })
                    : translate('support_setup_page.setup_complete', { defaultValue: 'Setup complete' })}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={`${completedSteps}/${steps.length} complete`}
                  color="primary"
                />
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchSetupStatus}>
                  {translate('support_setup_page.refresh', { defaultValue: 'Refresh' })}
                </Button>
              </Stack>
            </Stack>
            <LinearProgress variant="determinate" value={progress} sx={{ mt: 2.5, height: 8, borderRadius: 1 }} />
          </Paper>

          <Grid container spacing={2}>
            {steps.map((step) => (
              <Grid item xs={12} md={6} xl={4} key={step.key}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    height: '100%',
                    borderColor: step.is_complete ? 'primary.light' : 'divider',
                  }}
                >
                  <Stack spacing={1.5} sx={{ height: '100%' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: 1.5,
                          display: 'grid',
                          placeItems: 'center',
                          color: 'primary.main',
                          bgcolor: step.is_complete ? 'primary.lighter' : 'background.neutral',
                        }}
                      >
                        {step.icon}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1">{step.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {step.label}
                        </Typography>
                      </Box>
                      {step.is_complete ? <CheckCircleIcon color="primary" /> : <RadioButtonUncheckedIcon color="disabled" />}
                    </Stack>
                    <Divider />
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                      {step.description}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Chip
                        size="small"
                        label={step.is_complete ? translate('support_setup_page.ready', { defaultValue: 'Ready' }) : translate('support_setup_page.pending', { defaultValue: 'Pending' })}
                        color={step.is_complete ? 'primary' : 'default'}
                        variant={step.is_complete ? 'filled' : 'outlined'}
                      />
                      {step.path && (
                        <Button size="small" variant="text" onClick={() => navigate(step.path)}>
                          {translate('support_setup_page.open_setup', { defaultValue: 'Open Setup' })}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Stack>
      )}
    </>
  );
};

export default ChatSetup;
